import { useCallback, useEffect, useRef, useState } from "react";
import {
  fetchAllListings,
  fetchListingsCount,
  fetchListingsPage,
} from "../utils/listingsQuery";
import { isListingComplete } from "../utils/incompleteChecks";

/**
 * Hybrid pagination for the CMS Property Search listings view.
 *
 *   - Browse mode (no search): server-side cursor pagination + aggregate
 *     count. Each page = pageSize reads.
 *   - Search mode (search non-empty): fetch every listing ONCE per
 *     session (cached in a ref), then filter + paginate client-side.
 *     First search pays N reads; subsequent searches in the same
 *     session are free and support case-insensitive substring across
 *     property_name, website_title, address, listing_type, category, id.
 */

const SEARCH_FIELDS = [
  "property_name",
  "website_title",
  "address",
  "listing_type",
  "category",
  "id",
];

function buildSearchableText(doc) {
  return SEARCH_FIELDS.map((f) => doc[f] || "")
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

function sortByName(a, b) {
  const an = (a.property_name || a.website_title || "").toLowerCase();
  const bn = (b.property_name || b.website_title || "").toLowerCase();
  if (an && !bn) return -1;
  if (!an && bn) return 1;
  if (an < bn) return -1;
  if (an > bn) return 1;
  return (a.id || "").localeCompare(b.id || "");
}

export function useListingsPagination({
  pageSize = 24,
  search = "",
  // "all" | "hidden" | "new" | "incomplete". Non-"all" forces search
  // (fetchall) mode so pagination is stable — otherwise browse-mode
  // cursor pages would return uneven counts after client-side filtering.
  filter = "all",
} = {}) {
  const isSearchMode =
    (search || "").trim() !== "" || filter !== "all";

  const [docs, setDocs] = useState([]);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isPaging, setIsPaging] = useState(false);
  const [error, setError] = useState(null);

  const cursorsRef = useRef([null]);
  const allDocsRef = useRef(null); // listings[] | null
  const reqIdRef = useRef(0);

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  // ─── Browse mode ───────────────────────────────────────────────────

  const fetchAtBrowse = useCallback(
    async (targetPage) => {
      const myReqId = ++reqIdRef.current;
      const isInitial =
        cursorsRef.current.length === 1 && targetPage === 1;
      if (isInitial) setIsLoading(true);
      else setIsPaging(true);
      setError(null);
      try {
        let highestKnown = cursorsRef.current.length - 1;
        while (highestKnown < targetPage - 1) {
          const cursor = cursorsRef.current[highestKnown];
          const result = await fetchListingsPage({
            pageSize,
            cursor,
            search: "",
          });
          if (myReqId !== reqIdRef.current) return;
          if (result.snaps.length === 0) break;
          cursorsRef.current.push(result.snaps[result.snaps.length - 1]);
          highestKnown += 1;
        }
        const cursor = cursorsRef.current[targetPage - 1] ?? null;
        const result = await fetchListingsPage({
          pageSize,
          cursor,
          search: "",
        });
        if (myReqId !== reqIdRef.current) return;
        if (
          result.snaps.length === pageSize &&
          cursorsRef.current.length === targetPage
        ) {
          cursorsRef.current.push(result.snaps[result.snaps.length - 1]);
        }
        setDocs(result.docs);
        setPage(targetPage);
      } catch (err) {
        if (myReqId !== reqIdRef.current) return;
        console.error("[useListingsPagination/browse]", err);
        setError(err);
      } finally {
        if (myReqId === reqIdRef.current) {
          setIsLoading(false);
          setIsPaging(false);
        }
      }
    },
    [pageSize],
  );

  const refreshBrowseCount = useCallback(async () => {
    try {
      const count = await fetchListingsCount({ search: "" });
      setTotalCount(count);
      return count;
    } catch (err) {
      console.error(err);
      return null;
    }
  }, []);

  // ─── Search mode ───────────────────────────────────────────────────

  const ensureAllDocs = useCallback(async () => {
    if (allDocsRef.current) return allDocsRef.current;
    const docs = await fetchAllListings();
    allDocsRef.current = docs;
    return docs;
  }, []);

  const filterCachedDocs = useCallback(
    (allDocs) => {
      const q = (search || "").trim().toLowerCase();
      return allDocs.filter((d) => {
        if (filter === "hidden" && d.showOnWebsite !== false) return false;
        if (filter === "new" && d.cmsOpenedAt) return false;
        if (filter === "incomplete" && isListingComplete(d)) return false;
        if (q && !buildSearchableText(d).includes(q)) return false;
        return true;
      });
    },
    [search, filter],
  );

  const fetchAtSearch = useCallback(
    async (targetPage) => {
      const myReqId = ++reqIdRef.current;
      const hasCached = allDocsRef.current !== null;
      if (!hasCached) setIsLoading(true);
      else setIsPaging(true);
      setError(null);
      try {
        const all = await ensureAllDocs();
        if (myReqId !== reqIdRef.current) return;

        const filtered = filterCachedDocs(all).sort(sortByName);
        const total = filtered.length;
        const newTotalPages = Math.max(1, Math.ceil(total / pageSize));
        const safeTarget = Math.min(Math.max(1, targetPage), newTotalPages);
        const start = (safeTarget - 1) * pageSize;
        const pageDocs = filtered.slice(start, start + pageSize);

        setDocs(pageDocs);
        setTotalCount(total);
        setPage(safeTarget);
      } catch (err) {
        if (myReqId !== reqIdRef.current) return;
        console.error("[useListingsPagination/search]", err);
        setError(err);
      } finally {
        if (myReqId === reqIdRef.current) {
          setIsLoading(false);
          setIsPaging(false);
        }
      }
    },
    [pageSize, ensureAllDocs, filterCachedDocs],
  );

  // ─── Effect: switch mode / reload when query shape changes ────────

  useEffect(() => {
    if (isSearchMode) {
      fetchAtSearch(1);
    } else {
      cursorsRef.current = [null];
      setPage(1);
      setDocs([]);
      refreshBrowseCount();
      fetchAtBrowse(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, filter, isSearchMode]);

  const goToPage = useCallback(
    (n) => {
      const target = Math.min(Math.max(1, n), totalPages);
      if (target === page && !isLoading) return;
      if (isSearchMode) fetchAtSearch(target);
      else fetchAtBrowse(target);
    },
    [
      fetchAtBrowse,
      fetchAtSearch,
      page,
      totalPages,
      isLoading,
      isSearchMode,
    ],
  );

  return {
    docs,
    page,
    totalCount,
    totalPages,
    pageSize,
    isLoading,
    isPaging,
    error,
    goToPage,
    isSearchMode,
  };
}
